import { Component, Fragment, useEffect, useState } from 'react';

/**
 * A failed optional training workarea must never take down the entire
 * recruitment profile. Keep the user's current page and provide an explicit
 * retry without saving or modifying any training or verification records.
 */
class TrainingWorkareaErrorBoundary extends Component {
  state = { failed: false, retryKey: 0 };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // Client-side diagnostics only; never log certificate contents or staff details.
    console.error('Training details could not render:', error);
  }

  retry = () => {
    this.setState(old => ({ failed: false, retryKey: old.retryKey + 1 }));
  };

  render() {
    if (this.state.failed) {
      return (
        <div role="alert" data-testid="training-workarea-error"
          className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-semibold">Training details could not open.</p>
          <p className="mt-1">Your saved certificates and review decisions are unchanged. You can retry this area or return to the training summary above.</p>
          <button type="button" onClick={this.retry}
            className="mt-3 min-h-10 rounded-lg border border-amber-600 bg-white px-3 py-2 font-semibold">
            Retry training details
          </button>
        </div>
      );
    }
    return <Fragment key={this.state.retryKey}>{this.props.children}</Fragment>;
  }
}

/**
 * The training summary is shown above this component. Keep certificate review
 * and Care Certificate assessment as separate, accessible work areas: never
 * imply that verifying training signs off induction or grants work readiness.
 * Only mount the selected detailed area to avoid duplicate network fanout.
 */
export default function TrainingAdminWorkspaceSwitcher({
  focusRecords = false,
  records,
  induction,
}) {
  const [active, setActive] = useState(focusRecords ? 'records' : null);

  useEffect(() => {
    // A certificate-review deep link must lead directly to its evidence work
    // area even when a manager has already opened the person's Training tab.
    if (focusRecords) setActive('records');
  }, [focusRecords]);

  const choices = [
    {
      id: 'records',
      title: 'Review certificates and training',
      subtitle: 'Mandatory courses, uploaded evidence and training history',
      testId: 'training-workspace-records',
    },
    {
      id: 'induction',
      title: 'Care Certificate and induction',
      subtitle: 'Worker answers, manager assessments and shadow-shift sign-off',
      testId: 'training-workspace-induction',
    },
  ];

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4"
      data-testid="training-admin-workspace-switcher"
      aria-label="Choose a training task area"
    >
      <div className="mb-3">
        <h3 className="text-base font-semibold text-slate-900">Choose what to work on</h3>
        <p className="mt-1 text-sm text-slate-600">
          Start with Training next steps above, or open one task area below.
          Verified training and induction assessments are separate checks.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {choices.map((choice) => (
          <button
            type="button"
            key={choice.id}
            data-testid={choice.testId}
            onClick={() => setActive(choice.id)}
            aria-pressed={active === choice.id}
            aria-controls={`training-workarea-${choice.id}`}
            className={`min-h-16 rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700 ${
              active === choice.id
                ? 'border-teal-700 bg-teal-50 text-teal-950'
                : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span className="block text-sm font-semibold">{choice.title}</span>
            <span className="mt-1 block text-xs font-normal text-slate-600">{choice.subtitle}</span>
          </button>
        ))}
      </div>
      {active === null && (
        <p className="mt-3 text-sm text-slate-600" data-testid="training-workspace-empty">
          Choose an area to see its records and actions. No existing checks are hidden or changed.
        </p>
      )}
      <div id="training-workarea-records" className="mt-4" role="region"
        aria-label="Certificates and mandatory training" data-testid="training-active-records"
        hidden={active !== 'records'}>
        {active === 'records' ? (
          <TrainingWorkareaErrorBoundary>{records}</TrainingWorkareaErrorBoundary>
        ) : null}
      </div>
      <div id="training-workarea-induction" className="mt-4" role="region"
        aria-label="Care Certificate and induction" data-testid="training-active-induction"
        hidden={active !== 'induction'}>
        {active === 'induction' ? (
          <TrainingWorkareaErrorBoundary>{induction}</TrainingWorkareaErrorBoundary>
        ) : null}
      </div>
    </section>
  );
}
